import React from "react";
import PropTypes from "prop-types";
import Spinner from "./Spinner";

// The "working on it" half of a page's top notification slot.
//
// Same box, same fixed 86px offset and same green palette as AlertNotification's
// success state, so an action reports its progress and its result in one place
// rather than two: a row action fired at the bottom of a long table used to say
// "Updating..." on a button the user had already scrolled past, while its result
// appeared up here.
//
// Two differences from AlertNotification. It never dismisses itself — it is
// mounted while a request is in flight and unmounted by the caller once the
// request settles — and it carries a "done / total" counter on the right, which
// is what tells the user how much work the one request is actually doing.
//
// The counter does not tick. Each of these saves is a single API call covering
// every record, so there is no per-record progress to report; the pair is there
// for the total, and the design shows it resting at 0 for the duration.
//
// The spinner is the shared components/Spinner (green by default, which is this
// toast's palette); it carries its own keyframes, so there is no <style> here.
const LoadingNotification = ({ message, completed, total }) => (
  <div
    role="status"
    aria-live="polite"
    style={{
      position: "fixed",
      top: "86px",
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 16px",
      borderRadius: "12px",
      boxShadow: "0 4px 10px 0 rgba(0, 0, 0, 0.12)",
      fontSize: "12px",
      fontWeight: 700,
      fontFamily: "Montserrat",
      lineHeight: "normal",
      gap: "40px",
      height: "50px",
      border: "1px solid var(--Green-500, #198754)",
      background: "var(--Green-100, #D1E7DD)",
      color: "var(--Green-800, #0A3622)",
      zIndex: 9999,
    }}
  >
    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
      <Spinner />
      <strong>{message}</strong>
    </div>
    {total != null && (
      <span style={{ color: "#6C757D", fontWeight: 600, whiteSpace: "nowrap" }}>
        {completed} / {total}
      </span>
    )}
  </div>
);

LoadingNotification.propTypes = {
  message: PropTypes.string,
  completed: PropTypes.number,
  total: PropTypes.number,
};

LoadingNotification.defaultProps = {
  message: "Saving...",
  completed: 0,
  total: null,
};

export default LoadingNotification;
